import MenuBookIcon from '@mui/icons-material/MenuBook';
import { Button, MenuItem, TextField } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import cn from 'classnames';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AddressBookSidebar, InputLabel, Selector } from '../../../components';
import Loader from '../../../components/Loader/Loader';
import { Label } from '../../../components/SummaryComponentMUI/components/common';
import { ShippingFlowSelectorOptionsEnum } from '../../../constants/addressBook';
import { fetchAddressBookRequest } from '../../../store/actions/addressBook';
import { selectAddressBook, selectIsAddressBookLoading } from '../../../store/selectors/addressBook';

import { selectTaxRequiredCountries } from '../../../store/selectors/shell';
import { ISelectorValue } from '../../../types/shell';
import { ICommonAddress, IOneLinkShippingDetails } from '../../../types/shipping';
import { getTaxFieldConfig, isObjectEmpty } from '../../../utils/helpers';
import { AddressPreview, GroupShipmentToggle, Message } from '../common';
import { SidebarModesEnum } from '../types';
import {
  getInitialSelectorState,
  getSelectOptions,
  isOfficeDropshipToggleVisible,
  resolveOfficeDropshipEnabled,
} from '../utils';

import styles from '../ShippingDetailsContainer.module.scss';

const AddressBookSidebarContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "sidebar-address-book" */ '../../AddressBookSidebarContainer/AddressBookSidebarContainer'
    ),
);
const ReceiverFixedAddressSidebarContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "sidebar-fixed-address" */ '../../ReceiverFixedAddressSidebarContainer/ReceiverFixedAddressSidebarContainer'
    ),
);

interface IProps {
  values: IOneLinkShippingDetails;
  onChange?: (v: IOneLinkShippingDetails) => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  mui?: boolean;
  maxHeightList?: boolean;
  error?: boolean | undefined;
}

const StyledSelector = styled(TextField)({
  '& .MuiSelect-select.Mui-disabled ~ .MuiSelect-icon': {
    display: 'none',
  },
});

const OneLink: React.FC<IProps> = ({
  values,
  onChange,
  isDisabled,
  isReadOnly,
  mui = false,
  maxHeightList = false,
  error = false,
}) => {
  const [sidebarMode, setSidebarMode] = React.useState<SidebarModesEnum>(SidebarModesEnum.None);
  const addressBook = useSelector(selectAddressBook);
  const isAddressBookLoading = useSelector(selectIsAddressBookLoading);
  const taxRequiredCountries = useSelector(selectTaxRequiredCountries);

  const dispatch = useDispatch();

  const {
    org_address_ids,
    is_receiver_address_fixed = false,
    receiver_address,
    is_office_dropship_enabled = false,
  } = values;
  const { country, tax_number } = receiver_address || {};

  const theme = useTheme();

  const [selectValue, setSelectValue] = React.useState<ShippingFlowSelectorOptionsEnum>(
    getInitialSelectorState({ is_receiver_address_fixed, address: receiver_address, is_delayed_shipping: true }),
  );

  const handleSelectChange = React.useCallback(
    (value: ShippingFlowSelectorOptionsEnum) => {
      let updates: IOneLinkShippingDetails;
      switch (value) {
        case ShippingFlowSelectorOptionsEnum.Fixed: {
          if (org_address_ids?.length === 1) {
            const address = addressBook?.find((a) => a.uid === org_address_ids[0]);
            updates = {
              is_receiver_address_fixed: true,
              org_address_ids: undefined,
              receiver_address: address,
            };
          } else {
            setSidebarMode(SidebarModesEnum.FixedAddress);
            updates = {
              is_receiver_address_fixed: true,
              org_address_ids: undefined,
              receiver_address: undefined,
            };
          }
          break;
        }
        case ShippingFlowSelectorOptionsEnum.RecipientDecides: {
          updates = {
            is_receiver_address_fixed: false,
            receiver_address: undefined,
            org_address_ids: org_address_ids || undefined,
          };
          break;
        }
        case ShippingFlowSelectorOptionsEnum.RecipientDecidesFromList: {
          updates = {
            is_receiver_address_fixed: true,
            receiver_address: undefined,
            org_address_ids: org_address_ids || undefined,
          };
          break;
        }
        default:
          return;
      }
      setSelectValue(value);
      const nextValues = { ...values, ...updates };
      onChange?.({
        ...nextValues,
        is_office_dropship_enabled: resolveOfficeDropshipEnabled({ ...nextValues, selectValue: value }),
      });
    },
    [onChange, values, addressBook, org_address_ids],
  );

  const handleAddressBookSubmit = React.useCallback(
    (addressIds: string[]) => {
      const nextValues = {
        ...values,
        is_receiver_address_fixed,
        org_address_ids: addressIds,
        receiver_address: undefined,
      };
      onChange?.({
        ...nextValues,
        is_office_dropship_enabled: resolveOfficeDropshipEnabled({ ...nextValues, selectValue }),
      });
    },
    [onChange, values, is_receiver_address_fixed, selectValue],
  );

  const options = useMemo(() => getSelectOptions(true), []); // isDelayedShipping = always true

  const handleCloseSidebar = React.useCallback(() => {
    setSidebarMode(SidebarModesEnum.None);
  }, []);

  const isEmptyRecipientFixedAddress = React.useMemo(() => isObjectEmpty(receiver_address), [receiver_address]);

  const isDropshipToggleVisible = useMemo(
    () => isOfficeDropshipToggleVisible({ selectValue, org_address_ids, receiver_address }),
    [selectValue, org_address_ids, receiver_address],
  );

  const handleDropshipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.({ ...values, is_office_dropship_enabled: e.currentTarget.checked });
    },
    [onChange, values],
  );

  const isFixedSelectorValue = useMemo(() => {
    return selectValue === ShippingFlowSelectorOptionsEnum.Fixed;
  }, [selectValue]);

  const actionButtonTitle = useMemo(() => {
    switch (true) {
      case isFixedSelectorValue:
        return `${!isEmptyRecipientFixedAddress ? 'Edit' : 'Enter'} fixed address`;
      case org_address_ids && org_address_ids.length !== 0:
        return 'Edit address book option';
      default:
        return 'Select address book option';
    }
  }, [isFixedSelectorValue, org_address_ids, isEmptyRecipientFixedAddress]);

  const addressPreviewProps = useMemo(
    () => ({
      addresses: org_address_ids,
      receiverAddress: isFixedSelectorValue ? receiver_address : undefined,
      forceAddressList: !isFixedSelectorValue,
      onReset: mui
        ? undefined
        : () =>
            onChange?.({
              receiver_address: undefined,
              is_receiver_address_fixed,
              org_address_ids,
            }),
      maxHeightList,
    }),
    [maxHeightList, mui, onChange, is_receiver_address_fixed, isFixedSelectorValue, org_address_ids, receiver_address],
  );

  const taxInputLabel = useMemo(() => {
    if (tax_number) {
      return getTaxFieldConfig(country, taxRequiredCountries).label;
    }
  }, [tax_number, country, taxRequiredCountries]);

  const handleSubmitFixedAddress = useCallback(
    (address: ICommonAddress) =>
      onChange?.({ ...values, receiver_address: address, is_receiver_address_fixed, org_address_ids }),
    [onChange, values, is_receiver_address_fixed, org_address_ids],
  );

  const selector = useMemo(() => {
    return mui ? (
      <StyledSelector
        disabled={isReadOnly}
        select
        fullWidth
        label="Address options"
        variant="outlined"
        value={selectValue}
        onChange={(e) => handleSelectChange(e.target.value as ShippingFlowSelectorOptionsEnum)}
        sx={{ mb: 2 }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </StyledSelector>
    ) : (
      <Selector
        className={styles.selector}
        helperText="Address options"
        options={options}
        value={options.find((option) => option.value === selectValue)}
        onChange={(v) => handleSelectChange((v as ISelectorValue).value as ShippingFlowSelectorOptionsEnum)}
        isClearable={false}
        isSearchable={false}
        closeMenuOnSelect
        readOnly={isReadOnly}
      />
    );
  }, [mui, options, selectValue, isReadOnly, handleSelectChange]);

  useEffect(() => {
    if (!addressBook && !isAddressBookLoading) {
      dispatch(fetchAddressBookRequest());
    }
  }, [dispatch, addressBook, isAddressBookLoading]);

  // this effect is needed to synchronize the selector value with the initial state
  useEffect(() => {
    setSelectValue(
      getInitialSelectorState({
        is_receiver_address_fixed,
        address: receiver_address,
        is_delayed_shipping: true,
      }),
    );
  }, [is_receiver_address_fixed, receiver_address]);

  return (
    <div className={cn(styles.container, styles.nested, { [styles.disabled]: isDisabled, [styles.mui]: mui })}>
      {!mui ? <h2 className={styles.title}>Shipping</h2> : null}
      {isDropshipToggleVisible && (
        <GroupShipmentToggle
          checked={is_office_dropship_enabled}
          onChange={!isReadOnly ? handleDropshipChange : undefined}
          disabled={isDisabled || isReadOnly}
        />
      )}
      {selector}
      {!isReadOnly && !isDisabled && (
        <Button
          variant="outlined"
          fullWidth
          onClick={() =>
            setSidebarMode(isFixedSelectorValue ? SidebarModesEnum.FixedAddress : SidebarModesEnum.AddressBook)
          }
          size="large"
          sx={{ minHeight: 50, mb: 3 }}
          startIcon={<MenuBookIcon />}
        >
          {actionButtonTitle}
        </Button>
      )}
      {!isDisabled && (
        <>
          {mui && (org_address_ids || receiver_address) && (
            <Label sx={{ color: error ? theme.sass.errorRed : undefined }}>Selected address options</Label>
          )}
          <AddressPreview {...addressPreviewProps} />
          {receiver_address?.tax_number && (
            <div className={styles.taxNumber}>
              <InputLabel value={taxInputLabel} />
              <span>{receiver_address?.tax_number}</span>
            </div>
          )}
          <Message selectValue={selectValue} addresses={org_address_ids} />
        </>
      )}
      <AddressBookSidebar onClose={handleCloseSidebar} trigger={sidebarMode === SidebarModesEnum.AddressBook}>
        <Suspense fallback={<Loader isLoading />}>
          <AddressBookSidebarContainer
            onClose={handleCloseSidebar}
            addressIds={org_address_ids}
            onSubmit={handleAddressBookSubmit}
            isSidebarOpen={sidebarMode === SidebarModesEnum.AddressBook}
          />
        </Suspense>
      </AddressBookSidebar>
      {sidebarMode === SidebarModesEnum.FixedAddress && (
        <Suspense fallback={<Loader isLoading />}>
          <ReceiverFixedAddressSidebarContainer
            addressBook={addressBook}
            trigger={sidebarMode === SidebarModesEnum.FixedAddress}
            onClose={handleCloseSidebar}
            onSubmit={mui ? handleSubmitFixedAddress : undefined}
            value={receiver_address}
            hideTaxField={true}
          />
        </Suspense>
      )}
    </div>
  );
};

export default OneLink;
